import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "../supabase";
import { team as teamDefaults } from "../data/mockData";
import ProgressBar from "../components/ProgressBar";
import PlayerCard from "../components/PlayerCard";
import GoalCard from "../components/GoalCard";
import DonateModal from "../components/DonateModal";
import ScheduleSection from "../components/ScheduleSection";
import Footer from "../components/Footer";

export default function TeamPage() {
  const [showDonate, setShowDonate] = useState(false);
  const [donations, setDonations] = useState([]);
  const [players, setPlayers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [goalCategories, setGoalCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const [donRes, playerRes, settingsRes, catRes] = await Promise.all([
      supabase.from("donations").select("*").order("created_at", { ascending: false }),
      supabase.from("players").select("*").eq("is_active", true).order("jersey_number"),
      supabase.from("coach_settings").select("*").limit(1).single(),
      supabase.from("goal_categories").select("*").order("sort_order"),
    ]);

    if (donRes.data) setDonations(donRes.data);
    if (playerRes.data) setPlayers(playerRes.data);
    if (settingsRes.data) setSettings(settingsRes.data);
    if (catRes.data) setGoalCategories(catRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const teamGoal = settings?.team_goal || teamDefaults.goal;
  const teamName = settings?.team_name || teamDefaults.name;
  const displayMode = settings?.display_mode || "team";
  const totalRaised = donations.reduce((sum, d) => sum + Number(d.amount), 0);
  const goalReached = totalRaised >= teamGoal;
  const totalDonors = donations.length;

  // Countdown / auto-close based on end_date in coach_settings
  const seasonEnd = settings?.end_date
    ? new Date(settings.end_date + "T23:59:59")
    : null;
  const now = new Date();
  const ended = seasonEnd ? now >= seasonEnd : false;
  const daysLeft = seasonEnd
    ? Math.max(0, Math.ceil((seasonEnd - now) / (1000 * 60 * 60 * 24)))
    : null;
  const fundraiserClosed = (settings?.fundraiser_closed || false) || ended;

  const playerRaised = {};
  donations.forEach((d) => {
    if (d.player_id) {
      playerRaised[d.player_id] = (playerRaised[d.player_id] || 0) + Number(d.amount);
    }
  });

  const playersWithTotals = players.map((p) => ({
    id: p.id,
    name: p.name,
    number: p.jersey_number,
    goal: p.goal_amount,
    raised: playerRaised[p.id] || 0,
  }));

  const recentDonors = donations.slice(0, 5);
  const pct = teamGoal > 0 ? Math.round((totalRaised / teamGoal) * 100) : 0;

  return (
    <div className="min-h-screen pb-24">
      <Helmet>
        <title>Step Up to the Plate — Buchtel Griffins Baseball Program</title>
        <meta property="og:title" content="Step Up to the Plate — Buchtel Griffins Baseball Program" />
        <meta property="og:description" content="Help the Buchtel Griffins Baseball Program reach their $5,000 season goal. Every dollar puts a kid on the field." />
        <meta property="og:image" content={`${window.location.origin}/BBall@2x.png`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Step Up to the Plate — Buchtel Griffins Baseball Program" />
        <meta name="twitter:description" content="Help the Buchtel Griffins Baseball Program reach their $5,000 season goal. Every dollar puts a kid on the field." />
        <meta name="twitter:image" content={`${window.location.origin}/BBall@2x.png`} />
      </Helmet>

      {/* Countdown banner */}
      {!ended && daysLeft !== null && daysLeft > 0 && (
        <div className="bg-red-600 text-white text-center py-2.5 text-sm font-semibold">
          {daysLeft === 1
            ? "Last day to donate!"
            : `Only ${daysLeft} days left to donate!`}
        </div>
      )}

      {/* Hero */}
      <header className="bg-navy text-white">
        <div className="max-w-3xl mx-auto px-5 py-12 text-center">
          <img
            src="/BBall@2x.png"
            alt="Buchtel Griffins Baseball Program"
            className="w-24 h-24 mx-auto mb-4 object-contain invert"
          />

          <p className="text-white/80 text-sm font-semibold tracking-widest uppercase mb-2">
            {teamDefaults.city} &middot; {settings?.season_year || teamDefaults.season} Season
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-3">
            {teamName}
          </h1>
          <p className="text-white/70 max-w-md mx-auto leading-relaxed text-lg">
            {teamDefaults.mission}
          </p>

        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5">
        {/* Fundraiser Ended — Wrap-up card */}
        {ended && !loading && (
          <section className="-mt-6 bg-white rounded-xl border border-warm-gray shadow-sm p-6 mb-8 text-center">
            <p className="text-xs font-semibold tracking-widest uppercase text-navy/50 mb-2">
              Fundraiser Complete
            </p>
            <h2 className="font-display text-3xl font-bold text-navy mb-3">
              Thank you, {teamDefaults.city}!
            </h2>
            <p className="text-navy/70 mb-5 leading-relaxed">
              The {teamName} fundraiser has ended. Together we raised{" "}
              <span className="font-bold text-navy">${totalRaised.toLocaleString()}</span>
              {" "}from{" "}
              <span className="font-bold text-navy">
                {totalDonors} donor{totalDonors !== 1 ? "s" : ""}
              </span>.
            </p>
            <a
              href="mailto:jedwards@pspeople.org?subject=New%20Fundraiser%20for%20My%20Sports%20Team&body=Hi%20PSP%20team%2C%0D%0A%0D%0AI%27d%20like%20to%20set%20up%20a%20new%20fundraiser%20for%20my%20sports%20team.%20Please%20get%20in%20touch%20to%20discuss%20next%20steps.%0D%0A%0D%0AThanks!"
              className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors text-sm"
            >
              Contact PSP to set up a new fundraiser for your sports team
            </a>
          </section>
        )}

        {/* Fundraising Progress */}
        {!ended && (
        <section className="-mt-6 bg-white rounded-xl border border-warm-gray shadow-sm p-6 mb-8">
          {loading ? (
            <div className="text-center text-navy/40 py-4">Loading...</div>
          ) : (
            <>
              {goalReached && (
                <div className="text-center mb-4 py-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-lg font-bold text-green-700">
                    Goal Reached! Keep the momentum going!
                  </p>
                </div>
              )}
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <span className="text-3xl font-bold text-navy">
                    ${totalRaised.toLocaleString()}
                  </span>
                  <span className="text-navy/40 text-sm ml-1.5">
                    of ${teamGoal.toLocaleString()} goal
                  </span>
                </div>
                <span className="text-sm font-semibold text-black">{pct}%</span>
              </div>
              <ProgressBar raised={totalRaised} goal={teamGoal} size="lg" />
              <div className="flex items-center justify-between mt-4 text-sm text-navy/50">
                <span>
                  <span className="font-semibold text-navy">{totalDonors}</span> donor{totalDonors !== 1 ? "s" : ""}
                </span>
                <span>
                  {goalReached
                    ? `$${(totalRaised - teamGoal).toLocaleString()} over goal!`
                    : `$${(teamGoal - totalRaised).toLocaleString()} to go`}
                </span>
              </div>
            </>
          )}
        </section>
        )}

        {/* Goal Breakdown */}
        {goalCategories.length > 0 && (
          <section className="mb-10">
            <h2 className="font-display text-2xl font-bold text-navy mb-5 text-center">
              Where Your Money Goes
            </h2>
            <div className={`grid grid-cols-1 gap-4 ${goalCategories.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
              {goalCategories.map((item) => (
                <GoalCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        )}

        {/* Players */}
        <section className="mb-10">
          <h2 className="font-display text-2xl font-bold text-navy mb-5 text-center">
            Meet the Team
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {playersWithTotals.map((player) => (
              <PlayerCard key={player.id} player={player} fundraiserClosed={fundraiserClosed} displayMode={displayMode} />
            ))}
          </div>
        </section>

        {/* Schedule */}
        <ScheduleSection />

        {/* Recent supporters */}
        {recentDonors.length > 0 && (
          <section className="mb-10">
            <h2 className="font-display text-2xl font-bold text-navy mb-5 text-center">
              Recent Supporters
            </h2>
            <div className="space-y-3">
              {recentDonors.map((d) => (
                <div key={d.id} className="bg-white rounded-lg border border-warm-gray p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-navy/10 flex items-center justify-center shrink-0">
                    <span className="text-navy/60 text-xs font-bold">
                      {d.is_anonymous ? "?" : (d.donor_name || "?").charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-sm text-navy truncate block">
                      {d.is_anonymous ? "Anonymous" : d.donor_name}
                    </span>
                    {d.message && (
                      <p className="text-xs text-navy/50 truncate">"{d.message}"</p>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-black">${Number(d.amount)}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />

      {/* Sticky CTA */}
      <div className="fixed bottom-0 inset-x-0 p-4 bg-cream/90 backdrop-blur-sm border-t border-warm-gray">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => !fundraiserClosed && setShowDonate(true)}
            disabled={fundraiserClosed}
            className={`w-full font-bold py-4 rounded-xl transition-colors text-lg shadow-sm ${
              fundraiserClosed
                ? "bg-warm-gray text-navy/40 cursor-not-allowed"
                : "bg-black hover:bg-neutral-800 text-white"
            }`}
          >
            {ended
              ? "Fundraiser Ended — Thank You!"
              : fundraiserClosed
              ? "Fundraiser Closed — Thank You!"
              : `Donate to the ${teamName}`}
          </button>
        </div>
      </div>

      {showDonate && (
        <DonateModal
          playerId={null}
          playerName={null}
          onClose={() => setShowDonate(false)}
          onDonated={() => { setShowDonate(false); fetchData(); }}
        />
      )}
    </div>
  );
}
